import { Autorenew } from '@mui/icons-material';
import CameraswitchIcon from '@mui/icons-material/Cameraswitch';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import ReplayIcon from '@mui/icons-material/Replay';
import { Box, Button, CircularProgress, IconButton, Link, Tooltip, Typography } from '@mui/material';
import { FC } from 'react';

import { useRDInstance } from 'src/hooks/useRdInstance';

type Props = {
  onChooseCamera: VoidFunction;
};

type ErrorProps = {
  onClick: () => void;
}

type SelectCameraProps = {
  color: 'warning' | 'success',
  text: string,
  onClick: () => void,
}

const sxBox = { display: 'flex', flexDirection: 'row' };

const NoInstanceError: FC<ErrorProps> = ({ onClick }) => (
  <Box sx={sxBox}>
    <FiberManualRecordIcon color="error" sx={{ mr: 1 }} />
    <Typography variant="body2" mr={2}>
      Failed to connect to Received Connect. See{' '}
      <Link
        sx={{ color: 'inherit', textDecoration: 'underline' }}
        href="https://receiveddigital.com/help-center/admin/troubleshooting-received-connect/"
      >
        troubleshooting steps here.
      </Link>
    </Typography>
    <Button
      sx={{ minWidth: '75px', alignSelf: 'center' }}
      size="small"
      variant="soft"
      color="inherit"
      startIcon={<ReplayIcon />}
      onClick={onClick}
    >
      Retry
    </Button>
  </Box>
);

const NoCameraError: FC<ErrorProps> = ({ onClick }) => (
  <Box sx={sxBox} alignItems="center" width="100%">
    <FiberManualRecordIcon color="error" sx={{ mr: 1 }} />
    <Typography variant="body2" sx={{ display: 'flex', flex: 1 }} mr={2}>
      No cameras found
    </Typography>
    <Button
      sx={{ minWidth: '90px' }}
      size="small"
      variant="soft"
      color="inherit"
      startIcon={<Autorenew />}
      onClick={onClick}
    >
      Refresh
    </Button>
  </Box>
);

const SelectCamera: FC<SelectCameraProps> = ({ color, text, onClick }) => (
  <Box sx={sxBox} alignItems="center">
    <FiberManualRecordIcon color={color} sx={{ mr: 1 }} />
    <Typography variant="body2">{text}</Typography>
    <Tooltip arrow title="Change Camera">
      <IconButton sx={{ ml: 2 }} aria-label="Change camera" onClick={onClick}>
        <CameraswitchIcon />
      </IconButton>
    </Tooltip>
  </Box>
);

const CameraStatus: FC<Props> = ({ onChooseCamera }) => {
  const { myConnectedInstance, getCameraData, isLoading, refresh } = useRDInstance();

  const cameraData = getCameraData();

  const isLoadingInstnceOrCameras = isLoading || cameraData.isLoading;
  const noInstance = !myConnectedInstance?.id;
  const noCameraList = !cameraData.cameras?.length;

  const defaultCameraSelected = !!cameraData.defaultCamera;

  const onRefreshClick = () => {
    refresh();
    cameraData.refresh();
  }

  switch (true) {
    case isLoadingInstnceOrCameras:
      return (
        <Box sx={sxBox}>
          <CircularProgress aria-label="Loading..." size={20} sx={{ mr: 1 }} />
          <Typography variant="body2">Connecting to camera...</Typography>
        </Box>
      );

    case noInstance:
      return <NoInstanceError onClick={onRefreshClick} />;

    case noCameraList:
      return <NoCameraError onClick={onRefreshClick} />;

    case !defaultCameraSelected:
      return <SelectCamera text="No camera selected" color="warning" onClick={onChooseCamera} />;

    default:
      return <SelectCamera text="Camera connected" color="success" onClick={onChooseCamera} />;
  }
};

export default CameraStatus;
